import React from 'react';
import { FileQuestion, Inbox, Search, Users, Briefcase, Calendar, AlertCircle } from 'lucide-react';
import { typography, colors } from '../../styles/design-tokens';
import UnifiedButton from './UnifiedButton';
import Icon from '../AppIcon';


/**
 * EmptyState Component
 * Consistent empty state messages with illustrations
 * 
 * @param {string} variant - Empty state type: 'no-data' | 'no-results' | 'error' | 'no-jobs' | 'no-candidates' | 'no-applications'
 * @param {string} title - Main heading text
 * @param {string} description - Supporting description text
 * @param {string} actionLabel - Button text
 * @param {function} onAction - Button click handler
 * @param {string} className - Additional CSS classes
 */
const EmptyState = ({
  variant = 'no-data',
  title,
  description,
  actionLabel,
  onAction,
  className = '',
}) => {
  // Icon mapping for different variants
  const iconMap = {
    'no-data': FileQuestion,
    'no-results': Search,
    'no-jobs': Briefcase,
    'no-candidates': Users,
    'no-applications': Inbox,
    'no-interviews': Calendar,
    'error': AlertCircle,
  };

  const Icon = iconMap?.[variant] || FileQuestion;

  // Default titles and descriptions based on variant
  const defaultContent = {
    'no-data': {
      title: 'No data available',
      description: 'There is no data to display at this time.',
    },
    'no-results': {
      title: 'No results found',
      description: 'Try adjusting your search or filter criteria.',
    },
    'no-jobs': {
      title: 'No jobs posted yet',
      description: 'Start by creating your first job posting to attract top talent.',
    },
    'no-candidates': {
      title: 'No candidates yet',
      description: 'Applications will appear here once candidates start applying.',
    },
    'no-applications': {
      title: 'No applications',
      description: 'You haven\'t applied to any jobs yet. Start exploring opportunities!',
    },
    'no-interviews': {
      title: 'No interviews scheduled',
      description: 'Your upcoming interviews will appear here.',
    },
    'error': {
      title: 'Something went wrong',
      description: 'We encountered an error loading this content. Please try again.',
    },
  };

  const displayTitle = title || defaultContent?.[variant]?.title || defaultContent?.['no-data']?.title;
  const displayDescription = description || defaultContent?.[variant]?.description || defaultContent?.['no-data']?.description;

  // Color scheme based on variant
  const colorClass = variant === 'error' ? 'text-red-500' : 'text-muted-foreground';

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`?.trim()}>
      <div className={`p-6 bg-muted/30 rounded-full mb-6`}>
        <Icon className={`w-12 h-12 ${colorClass}`} />
      </div>
      <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-2`}>
        {displayTitle}
      </h3>
      <p className={`${typography?.body?.default} ${colors?.text?.secondary} max-w-md mb-6`}>
        {displayDescription}
      </p>
      {actionLabel && onAction && (
        <UnifiedButton 
          variant={variant === 'error' ? 'secondary' : 'primary'} 
          size="medium"
          onClick={onAction}
        >
          {actionLabel}
        </UnifiedButton>
      )}
    </div>
  );
};

export default EmptyState;